import type { CSSProperties, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

type RevealTag = 'div' | 'section' | 'article' | 'li' | 'header' | 'figure' | 'p'

interface RevealProps {
    children: ReactNode
    as?: RevealTag
    delay?: number
    media?: boolean
    className?: string
    style?: CSSProperties
}

/* Fades and lifts a block the first time it enters the viewport, then stops
   observing it. The pre-animation state lives in CSS behind the .js class, so
   without scripting every block is simply visible from the start, and a
   visitor who prefers reduced motion skips straight to the final state. */
export const Reveal = ({
    children,
    as = 'div',
    delay = 0,
    media = false,
    className,
    style
}: RevealProps): ReactNode => {
    const ref = useRef<HTMLElement | null>(null)
    const [shown, setShown] = useState(false)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setShown(true)

            return
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return

                setShown(true)
                observer.unobserve(entry.target)
            })
        }, {
            rootMargin: '0px 0px -6% 0px',
            threshold: 0.08
        })

        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    const Tag = as as 'div'
    const state = shown ? 'in' : ''
    const attributes = media ? { 'data-reveal-media': state } : { 'data-reveal': state }

    return (
        <Tag
            ref={ref as never}
            {...attributes}
            className={className}
            style={delay ? { ...style, transitionDelay: `${delay}ms` } : style}
        >
            {children}
        </Tag>
    )
}
